import { HoldState, TicketTypeState } from "@/domain/ticket-selection";
import { ArrayHelper } from "webshop-modules/helper/Array.helper";
import { ObjectUtils } from "webshop-modules";
import { StepperState } from "@/components/atoms/stepper/stepper-state.interface";

/**
 * HoldStateProvider
 * data provider class for Hold price list level
 */
export class HoldStateProvider {

    /**
     * whatItDoes
     * reduces all available ticket type amounts to highest value
     */
    public static getHighestTicketTypeAmount(hold?: HoldState): number {
        return !hold ? 0 : hold.ticketTypes
            .map(ticketType => ticketType.amount)
            .reduce(ArrayHelper.ToHighestValue, 0);
    }

    /**
     * whatItDoes
     * reduces all available ticket type amounts to lowest max property value
     */
    public static getLowestTicketTypeMaximum(maxTickets: number, hold?: HoldState): number {
        return !hold ? maxTickets : hold.ticketTypes
            .map(ticketType => ticketType.max)
            .reduce(ArrayHelper.ToLowestValue, maxTickets);
    }

    /**
     * whatItDoes
     * returns available ticket amount
     */
    public static getAvailableTicketAmount(hold?: HoldState): number {
        return !hold ? 0 : hold.maxTickets - hold.ticketTypes.map(ticketType => ticketType.amount).reduce(ArrayHelper.ToSum);
    }

    /**
     * whatItDoes
     * updates active hold ticket types with provided state
     */
    public static updatedHoldForActiveTicketTypes(
        updatedState: TicketTypeState[],
        hold: HoldState
    ): HoldState {
        if (hold.active && hold.ticketTypes) {
            hold.ticketTypes = updatedState;
        }

        return hold;
    }

    /**
     * whatItDoes
     * provides updated state of TicketTypeCollection for stepper change
     */
    public static provideUpdatedTicketTypesFor(
        ticketTypeState: TicketTypeState,
        stepper: StepperState,
        hold: HoldState
    ): TicketTypeState[] {
        if (!hold.ticketTypes) {
            return [];
        }
        const availabilityDelta = ticketTypeState.amount - stepper.amount;

        return ObjectUtils.clone(hold).ticketTypes.map(ticketType => {
            const availability = this.getAvailableTicketAmount(hold) + availabilityDelta;

            if (ticketType.ticketTypeID === ticketTypeState.ticketTypeID && ticketType.priceId === ticketTypeState.priceId) {
                ticketType.amount = stepper.amount;
            }

            ticketType.canDecrease = ticketType.amount > 0;
            ticketType.canIncrease = ticketType.step <= availability && ticketType.max > ticketType.amount;

            return ticketType;
        });
    }

    /**
     * whatItDoes
     * validates combined ticket type amounts against hold amount restrictions
     */
    public static isAmountValidFor(hold: HoldState): boolean {
        const totalAmount = hold.ticketTypes
            .map(ticketType => ticketType.amount)
            .reduce(ArrayHelper.ToSum, 0);

        return totalAmount <= hold.maxTickets && totalAmount > 0;
    }

    /**
     * whatItDoes
     * validates all ticket type amounts against ticket type min/max restrictions
     */
    public static areTicketTypesValidIn(hold: HoldState): boolean {
        if (!hold.ticketTypes || hold.ticketTypes.length === 0) {
            return false;
        }

        return hold.ticketTypes
            .filter(ArrayHelper.ValidResults)
            .map(tt => tt.amount === 0 || (tt.amount > 0 && tt.amount >= tt.min && tt.amount <= tt.max))
            .reduce(ArrayHelper.ValidityReducer);
    }

}
